// components/home/listings/hotel/Hotel.jsx
import React, { useState, useEffect } from 'react';
import { getAllHotels } from '../../../../api/hotel/hotelApi';
import { useTranslation } from 'react-i18next';
import HotelComponent from './HotelComponent';
import SearchBar from './SearchBar';
import FilterModal from './FilterModal';
import './css/Hotel.css';
import Loader from '../../../common/Loader.jsx';
import Error from '../../../common/Error.jsx';
import useGeolocation from '../../../../hooks/useGeolocation';
import { parseSearchQuery, matchesParsedQuery } from '../../../../utils/parseSearchQuery';


const Hotel = () => {
    const { t } = useTranslation();
    const [hotels, setHotels] = useState([]);
    const [nearbyHotels, setNearbyHotels] = useState([]);
    const [filteredHotels, setFilteredHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const location = useGeolocation();
    const [filters, setFilters] = useState({
        name: '',
        city: '',
        location: '',
        minPrice: '',
        maxPrice: '',
        rating: '',
        amenities: []
    });

    // Nombre de filtres actifs (affiché sur le badge du bouton Filtres)
    const activeFilterCount = Object.entries(filters).reduce((count, [key, value]) => {
        if (Array.isArray(value)) return count + (value.length > 0 ? 1 : 0);
        return count + (value !== '' && value != null ? 1 : 0);
    }, 0);

    useEffect(() => {
        const fetchAllHotels = async () => {
            try {
                const allResponse = await getAllHotels();
                if (allResponse.success) {
                    setHotels(allResponse.data);
                    setFilteredHotels(allResponse.data);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchAllHotels();
    }, []);

    useEffect(() => {
        if (!location.loaded || !location.coordinates?.lat || !location.coordinates?.lng) return;
        const fetchNearby = async () => {
            try {
                const nearbyResponse = await getAllHotels({
                    latitude: location.coordinates.lat,
                    longitude: location.coordinates.lng,
                    radius: 50000
                });
                if (nearbyResponse.success) {
                    setNearbyHotels(nearbyResponse.data);
                }
            } catch (err) {
                console.error('Nearby hotels error:', err.message);
            }
        };
        fetchNearby();
    }, [location.loaded, location.coordinates?.lat, location.coordinates?.lng]);

    const handleSearch = (term) => {
        setSearchTerm(term);

        if (!term.trim() && Object.values(filters).every(val =>
            val === '' || (Array.isArray(val) && val.length === 0)
        )) {
            setFilteredHotels(hotels);
            return;
        }

        applyFilters(term, filters);
    };

    // La main d'indice ne s'affiche que sur la première carte visible de la page
    const firstIsNearby = location.loaded && !location.error && nearbyHotels.length > 0;

    const applyFilters = (searchTerm, appliedFilters) => {
        let filtered = [...hotels];

        // Apply smart search — parse natural language like "hotels in bamenda"
        if (searchTerm.trim()) {
            const parsed = parseSearchQuery(searchTerm);
            filtered = filtered.filter(hotel =>
                matchesParsedQuery(hotel, parsed, {
                    name: (h) => h.hotelName,
                    location: (h) => `${h.city || ''} ${h.address || ''}`,
                    description: (h) => h.description || '',
                    category: () => 'hotel',
                })
            );
        }

        // Apply name filter
        if (appliedFilters.name) {
            filtered = filtered.filter(hotel =>
                hotel.hotelName.toLowerCase().includes(appliedFilters.name.toLowerCase())
            );
        }

        // Apply city filter
        if (appliedFilters.city) {
            filtered = filtered.filter(hotel =>
                hotel.city.toLowerCase().includes(appliedFilters.city.toLowerCase())
            );
        }

        // Apply location filter
        if (appliedFilters.location) {
            filtered = filtered.filter(hotel =>
                hotel.address?.toLowerCase().includes(appliedFilters.location.toLowerCase())
            );
        }

        // Apply price filter
        if (appliedFilters.minPrice || appliedFilters.maxPrice) {
            filtered = filtered.filter(hotel => {
                const minRoomPrice = Math.min(...hotel.rooms.map(room => room.pricePerNight));

                if (appliedFilters.minPrice && minRoomPrice < parseInt(appliedFilters.minPrice)) return false;
                if (appliedFilters.maxPrice && minRoomPrice > parseInt(appliedFilters.maxPrice)) return false;
                return true;
            });
        }

        // Apply rating filter
        if (appliedFilters.rating) {
            filtered = filtered.filter(hotel =>
                hotel.ratings?.average >= parseInt(appliedFilters.rating)
            );
        }

        // Apply amenities filter
        if (appliedFilters.amenities.length > 0) {
            filtered = filtered.filter(hotel =>
                appliedFilters.amenities.every(amenity =>
                    hotel.amenities?.includes(amenity)
                )
            );
        }

        setFilteredHotels(filtered);
    };

    const handleApplyFilters = (appliedFilters) => {
        setFilters(appliedFilters);
        applyFilters(searchTerm, appliedFilters);
    };

    if (loading) return <Loader />;
    if (error) return <Error errMessage={error} />;

    return (
        <div className="fade-up hotel-listing-section">
            {/* En-tête de page */}
            <div className="hotel-page-hero">
                <span className="hotel-page-eyebrow">Spotfli</span>
                <h2 className="hotel-page-title">{t('hotel_page.title')}</h2>
                <p className="hotel-page-subtitle">{t('hotel_page.subtitle')}</p>
                <div className="hotel-page-flagline" aria-hidden="true">
                    <span /><span /><span />
                </div>
            </div>

            {/* Recherche + filtres */}
            <SearchBar
                onSearch={handleSearch}
                onFilterClick={() => setIsFilterModalOpen(true)}
                searchTerm={searchTerm}
                activeFilterCount={activeFilterCount}
            />

            {/* Nearby Hotels Section */}
            {location.loaded && !location.error && (
                <div className="nearby-hotels-section">
                    <h3 className="subsection-title">{t('hotel_page.nearby_title')}</h3>
                    {nearbyHotels.length > 0 ? (
                        <div className="hotel-listings-container nearby-grid">
                            {nearbyHotels.map((hotel, index) => (
                                <HotelComponent
                                    key={`nearby-${hotel._id}`}
                                    id={hotel._id}
                                    name={hotel.hotelName}
                                    images={hotel.images}
                                    city={hotel.city}
                                    description={hotel.description}
                                    rating={hotel.ratings?.average || 0}
                                    ratingCount={hotel.ratings?.count || 0}
                                    starRating={hotel.starRating}
                                    rooms={hotel.rooms}
                                    isFirst={firstIsNearby && index === 0}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="no-nearby-message">
                            <p>{t('hotel_page.no_nearby')}</p>
                        </div>
                    )}
                    <hr className="section-divider" />
                </div>
            )}

            {/* Résultats */}
            <div className="results-info">
                <p className="results-count">
                    <strong>{filteredHotels.length}</strong>{' '}
                    {t('hotel_page.results')}
                </p>
            </div>

            <div className="hotel-listings-container">
                {filteredHotels.length > 0 ? (
                    filteredHotels.map((hotel, index) => (
                        <HotelComponent
                            key={hotel._id}
                            id={hotel._id}
                            name={hotel.hotelName}
                            images={hotel.images}
                            city={hotel.city}
                            description={hotel.description}
                            rating={hotel.ratings?.average || 0}
                            ratingCount={hotel.ratings?.count || 0}
                            starRating={hotel.starRating}
                            rooms={hotel.rooms}
                            isFirst={!firstIsNearby && index === 0}
                        />
                    ))
                ) : (
                    <div className="no-hotels">
                        <p>{t('hotel_page.results_empty')}</p>
                        <button
                            className="clear-filters-btn"
                            onClick={() => {
                                setSearchTerm('');
                                setFilters({
                                    name: '',
                                    city: '',
                                    location: '',
                                    minPrice: '',
                                    maxPrice: '',
                                    rating: '',
                                    amenities: []
                                });
                                setFilteredHotels(hotels);
                            }}
                        >
                            {t('hotel_page.clear_filters')}
                        </button>
                    </div>
                )}
            </div>

            {/* Modale de filtres */}
            <FilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                onApplyFilters={handleApplyFilters}
                filters={filters}
            />
        </div>
    );
};

export default Hotel;
